import React, { useState, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { CartProvider } from './context/CartContext';
import { AdminProvider } from './context/AdminContext';
import { UserProvider } from './context/UserContext';
import { OAuthProvider, useOAuth } from './context/OAuthContext';
import LoadingScreen from './components/LoadingScreen';
import HeroSection from './components/HeroSection';
import VelouraLanding from './pages/VelouraLanding';
import VagueLanding from './pages/VagueLanding';
import Shop from './pages/Shop';
import Checkout from './pages/Checkout';
import Confirmation from './pages/Confirmation';
import PaymentGateway from './pages/PaymentGateway';
import PurchaseComplete from './pages/PurchaseComplete';
import Admin from './pages/Admin';
import Careers from './pages/Careers';
import Partners from './pages/Partners';
import News from './pages/News';
import GalleryPage from './pages/GalleryPage';
import ApplicationView from './pages/ApplicationView';
import UserAccount from './pages/UserAccount';
import OAuthCallback from './pages/OAuthCallback';
import FloatingCart from './components/shop/FloatingCart';
import { siteSettingsAPI } from './services/api';
import './App.css';
import Studios from './pages/Studios';
import UnderMaintenance from './pages/UnderMaintenance';
import Team from './pages/Team';
import Publications from './pages/Publications';
import PublicationReader from './pages/PublicationReader';
import {
  LEGACY_MAINTENANCE_SETTING,
  MAINTENANCE_PAGES_SETTING,
  createMaintenanceState,
  getMaintenancePageForPath,
  parseMaintenanceState
} from './config/maintenancePages';

const MAINTENANCE_TARGET_KEY = 'maintenance_target_path';

const getStoredMaintenanceTarget = () => {
  try {
    return window.sessionStorage.getItem(MAINTENANCE_TARGET_KEY);
  } catch (error) {
    return null;
  }
};

const setStoredMaintenanceTarget = (pathname) => {
  try {
    window.sessionStorage.setItem(MAINTENANCE_TARGET_KEY, pathname);
  } catch (error) {
    // The current path is also carried in router state when storage is unavailable.
  }
};

const clearStoredMaintenanceTarget = () => {
  try {
    window.sessionStorage.removeItem(MAINTENANCE_TARGET_KEY);
  } catch (error) {
    // Nothing else is required if storage is unavailable.
  }
};

function AppContent() {
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState('veloura');
  const [maintenancePages, setMaintenancePages] = useState(() => createMaintenanceState(false));
  const [maintenanceLoaded, setMaintenanceLoaded] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const prevPathnameRef = React.useRef(location.pathname);
  const isInitialMountRef = React.useRef(true);
  const { currentUser, isInitialized: oauthInitialized, logout } = useOAuth();

  useEffect(() => {
    if (currentUser) {
      console.log('Location changed (logged in):', location.pathname, 'Previous:', prevPathnameRef.current);
    }
  }, [location.pathname, currentUser]);

  useEffect(() => {
    const applyMaintenanceSettings = (settings) => {
      setMaintenancePages(parseMaintenanceState(
        settings?.[MAINTENANCE_PAGES_SETTING],
        settings?.[LEGACY_MAINTENANCE_SETTING]
      ));
      setMaintenanceLoaded(true);
    };

    const loadMaintenanceMode = async () => {
      try {
        const response = await siteSettingsAPI.get(
          `${MAINTENANCE_PAGES_SETTING},${LEGACY_MAINTENANCE_SETTING}`
        );

        if (response.success && response.data) {
          applyMaintenanceSettings(response.data);
        }
      } catch (error) {
        console.error('Error loading maintenance mode:', error);
        setMaintenancePages(createMaintenanceState(false));
      } finally {
        setMaintenanceLoaded(true);
      }
    };

    const handleSettingsUpdated = (event) => {
      const settings = event.detail;
      if (
        settings
        && (
          Object.prototype.hasOwnProperty.call(settings, MAINTENANCE_PAGES_SETTING)
          || Object.prototype.hasOwnProperty.call(settings, LEGACY_MAINTENANCE_SETTING)
        )
      ) {
        applyMaintenanceSettings(settings);
      }
    };

    loadMaintenanceMode();
    window.addEventListener('siteSettingsUpdated', handleSettingsUpdated);

    return () => window.removeEventListener('siteSettingsUpdated', handleSettingsUpdated);
  }, []);

  const handleLoadingComplete = () => {
    setIsLoading(false);
    window.scrollTo(0, 0);
  };

  const handleBrandChange = (brand) => {
    if (brand === 'vague') {
      return;
    }
    if (brand !== selectedBrand) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setSelectedBrand(brand);
    }
  };

  useLayoutEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      prevPathnameRef.current = location.pathname;
      return;
    }

    const prevPathname = prevPathnameRef.current;
    const currentPathname = location.pathname;

    if (prevPathname === currentPathname) {
      return;
    }

    const isShopPage = currentPathname === '/shop' || currentPathname.startsWith('/shop');
    const wasShopPage = prevPathname === '/shop' || prevPathname.startsWith('/shop');
    const isMainPage = currentPathname === '/' || (!isShopPage && currentPathname !== '/checkout' && !currentPathname.startsWith('/confirmation') && currentPathname !== '/careers' && currentPathname !== '/partners' && currentPathname !== '/news' && currentPathname !== '/publications' && !currentPathname.startsWith('/publication/') && !currentPathname.startsWith('/studios') && !currentPathname.startsWith('/team') && currentPathname !== '/maintenance' && currentPathname !== '/account' && currentPathname !== '/admin' && !currentPathname.startsWith('/application') && !currentPathname.startsWith('/gallery'));
    const wasMainPage = prevPathname === '/' || (!wasShopPage && prevPathname !== '/checkout' && !prevPathname.startsWith('/confirmation') && prevPathname !== '/careers' && prevPathname !== '/partners' && prevPathname !== '/news' && prevPathname !== '/publications' && !prevPathname.startsWith('/publication/') && !prevPathname.startsWith('/studios') && !prevPathname.startsWith('/team') && prevPathname !== '/maintenance' && prevPathname !== '/account' && prevPathname !== '/admin' && !prevPathname.startsWith('/application') && !prevPathname.startsWith('/gallery'));

    if ((isShopPage && wasMainPage) || (isMainPage && wasShopPage)) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }

    prevPathnameRef.current = currentPathname;
  }, [location.pathname]);






  useEffect(() => {
    if (location.state && location.state.selectedBrand) {
      const brand = location.state.selectedBrand;
      if (brand === 'vague') {
        setSelectedBrand('veloura');
        if (location.state.scrollTo) {
          setTimeout(() => {
            const section = document.getElementById(location.state.scrollTo);
            if (section) {
              section.scrollIntoView({ behavior: 'smooth' });
            }
          }, 500);
        }
      } else {
        setSelectedBrand(brand);
        window.scrollTo(0, 0);

        if (location.state.scrollTo) {
          setTimeout(() => {
            const section = document.getElementById(location.state.scrollTo);
            if (section) {
              section.scrollIntoView({ behavior: 'smooth' });
            }
          }, 500);
        }
      }
    }
  }, [location.state]);

  const getLoadingTitle = () => {
    if (location.pathname === '/shop' || location.pathname.startsWith('/shop')) {
      return 'SHOP';
    }
    const brand = location.state?.selectedBrand || selectedBrand;
    return 'VELOURA';
  };

  const isAdminPage = location.pathname === '/admin' || location.pathname.startsWith('/admin');
  const isAuthCallback = location.pathname === '/auth/callback';
  const isMaintenancePage = location.pathname === '/maintenance';
  const maintenanceTargetPath = isMaintenancePage
    ? (location.state?.maintenanceTarget || getStoredMaintenanceTarget() || '/')
    : location.pathname;
  const maintenancePage = getMaintenancePageForPath(maintenanceTargetPath);
  const shouldShowMaintenance = maintenanceLoaded
    && !isAdminPage
    && !isAuthCallback
    && Boolean(maintenancePage && maintenancePages[maintenancePage.id]);

  useEffect(() => {
    if (shouldShowMaintenance && !isMaintenancePage) {
      setStoredMaintenanceTarget(location.pathname);
      navigate('/maintenance', {
        replace: true,
        state: { maintenanceTarget: location.pathname }
      });
      return;
    }

    if (maintenanceLoaded && !shouldShowMaintenance && isMaintenancePage) {
      const returnPath = maintenanceTargetPath && maintenanceTargetPath !== '/maintenance'
        ? maintenanceTargetPath
        : '/';
      clearStoredMaintenanceTarget();
      navigate(returnPath, { replace: true });
    }
  }, [
    shouldShowMaintenance,
    isMaintenancePage,
    maintenanceLoaded,
    maintenanceTargetPath,
    location.pathname,
    navigate
  ]);

        return (
          <>
            {}
            {isLoading && !isAdminPage && !isAuthCallback && !shouldShowMaintenance && (
              <LoadingScreen
                onLoadingComplete={handleLoadingComplete}
                title={getLoadingTitle()}
              />
            )}
            {}
            {(!isLoading || isAdminPage || isAuthCallback || shouldShowMaintenance) && (
              <>
                {}
                {!shouldShowMaintenance && (location.pathname.startsWith('/shop') || location.pathname === '/checkout' || location.pathname.startsWith('/confirmation')) && (
                  <FloatingCart />
                )}
                {shouldShowMaintenance ? (
                  <Routes>
                    <Route path="/maintenance" element={<UnderMaintenance />} />
                    <Route path="*" element={<Navigate to="/maintenance" replace />} />
                  </Routes>
                ) : (
                  <Routes>
            <Route
              path="/shop"
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
                >
                  <Shop />
                </motion.div>
              }
            />
            <Route
              path="/shop/:productId"
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
                >
                  <Shop />
                </motion.div>
              }
            />
            <Route
              path="/checkout"
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
                >
                  <Checkout />
                </motion.div>
              }
            />
            <Route
              path="/confirmation/:orderId"
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
                >
                  <Confirmation />
                </motion.div>
              }
            />
            <Route
              path="/paymentGateway"
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
                >
                  <PaymentGateway />
                </motion.div>
              }
            />
            <Route
              path="/purchase-complete/:orderId"
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
                >
                  <PurchaseComplete />
                </motion.div>
              }
            />
            <Route
              path="/admin"
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
                >
                  <Admin />
                </motion.div>
              }
            />
            <Route
              path="/careers"
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
                >
                  <Careers />
                </motion.div>
              }
            />
            <Route
              path="/partners"
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
                >
                  <Partners />
                </motion.div>
              }
            />
            <Route
              path="/news"
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
                >
                  <News />
                </motion.div>
              }
            />
            <Route
              path="/publications"
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
                >
                  <Publications />
                </motion.div>
              }
            />
            <Route
              path="/publication/:slug"
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
                >
                  <PublicationReader />
                </motion.div>
              }
            />
            <Route
              path="/application/:token"
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
                >
                  <ApplicationView />
                </motion.div>
              }
            />
            <Route
              path="/account"
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
                >
                  <UserAccount />
                </motion.div>
              }
            />
            <Route
              path="/studios"
              element={
                <motion.div
                 initial={{ opacity: 0 }}
                 animate={{ opacity: 1 }}
                 transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
               >
                 <Studios />
               </motion.div>
             }
           />
            <Route
              path="/studios/:galleryId"
              element={
                <motion.div
                 initial={{ opacity: 0 }}
                 animate={{ opacity: 1 }}
                 transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
               >
                 <Studios />
               </motion.div>
             }
           />
            <Route
              path="/team"
              element={
                <motion.div
                 initial={{ opacity: 0 }}
                 animate={{ opacity: 1 }}
                 transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
               >
                 <Team />
               </motion.div>
             }
           />
            <Route
              path="/team/:memberSlug"
              element={
                <motion.div
                 initial={{ opacity: 0 }}
                 animate={{ opacity: 1 }}
                 transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
               >
                 <Team />
               </motion.div>
             }
           />
            <Route
              path="/gallery/:id"
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
                >
                  <GalleryPage />
                </motion.div>
              }
            />
            <Route
              path="/auth/callback"
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, ease: [0.43, 0.13, 0.23, 0.96] }}
                >
                  <OAuthCallback />
                </motion.div>
              }
            />
            <Route
              path="/maintenance"
              element={<Navigate to="/" replace />}
            />
            <Route
              path="/*"
              element={
                <div className="App">
                  {}
                  <HeroSection
                    selectedBrand={selectedBrand}
                    onBrandChange={handleBrandChange}
                  />
                  {}
                  <AnimatePresence mode="wait">
                    {selectedBrand === 'veloura' && (
                      <motion.div
                        key="veloura"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                      >
                        <VelouraLanding selectedBrand={selectedBrand} />
                      </motion.div>
                    )}
                    {}
                    {

}
                  </AnimatePresence>

                </div>

              }
            />

          </Routes>
                )}

              </>

            )}
    </>

  );
}

function App() {
  return (
    <UserProvider>
      <AdminProvider>
        <OAuthProvider>
          <CartProvider>
            <Router>
              <AppContent />
            </Router>

          </CartProvider>

        </OAuthProvider>

      </AdminProvider>

    </UserProvider>

  );
}

export default App;
